<script>
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("PageBody.vue");
</script>

<script setup>
import deepmerge from "deepmerge";
import blocks from "@/views/blocks";
import EditableSection from "@/views/page/EditableSection.vue";
import { useEditorActions } from "@/views/tools/page/utils/useEditorActions";
import { computed, onMounted, provide } from "vue";
import draggable from "vuedraggable";
import { useDragAndDrop } from "./utils/useDragAndDrop";
import { CardEmpty } from "@easy/ui/cards";
import { PageIcon } from "@easy/ui/icons";
import { PAGE_SECTION_KINDS } from "@/apps/page/services/page.service";
import ToolProjectSection from "@/views/page/ToolProjectSection.vue";
import { useMutations, useState } from "@/store/vuex-composition-mappers";
import { useRenderPreviewStore } from "@/views/store/render-preview.store";

const props = defineProps({
    page: {
        type: Object,
        required: true,
    },
    isMinimap: {
        type: Boolean,
        default: false,
    },
});

const { preview } = useState("shared/view", ["preview"]);
const { setPageMode } = useMutations("shared/view", ["setPageMode"]);

const { createBlock, createTool, setOrderSections, addSection } = useEditorActions();
const { isDragging } = !props.isMinimap
    ? useDragAndDrop({
          onBlockDropped: createBlock,
          onToolDropped: createTool,
      })
    : { isDragging: false };

// Computed properties
const pageColors = computed(() => ({
    "--et-colors-brand": props.page.colors.brand,
    "--et-colors-background": props.page.colors.background,
    "--et-colors-text": props.page.colors.text,
    "--et-colors-card-background": props.page.colors.cardBackground,
    "--et-colors-card-text": props.page.colors.cardText,
    "--et-colors-border": props.page.colors.border,
}));

const pageSections = computed(() =>
    props.page.uuid
        ? props.page.sections
              .filter((section) => !section.hide && !section._deleted)
              .sort((a, b) => a.order - b.order)
        : []
);

const blockComponents = Object.entries(blocks).reduce((acc, [name, component]) => {
    return {
        ...acc,
        [name]: component,
    };
}, {});

logger.trace(blockComponents);

defineExpose({ blocks: blockComponents });

const sections = computed({
    get: () => pageSections.value,
    set: (value) => setOrderSections(value.map((section) => section.id)),
});

const hasSections = computed(() => Boolean(pageSections.value.length));

onMounted(() => {
    const { setIsPageMode } = useRenderPreviewStore();
    setPageMode(true);
    setIsPageMode(true);
    makePageVarColorsGloballyAvailable();
});

function makeStyleValues(sectionStyleValues = {}) {
    // merge page style values with section ones.
    return deepmerge(props.page?.colors, sectionStyleValues);
}

// this is for render function (on production) for tools like `cookie` and `coffee` to have access to that values
// it cannot be handle in div.easy-page element because it's too low in the hierarchy!
function makePageVarColorsGloballyAvailable() {
    const body = document.querySelector("body[data-easypage]");
    if (body) {
        for (const [cssVar, value] of Object.entries(pageColors.value)) {
            body.style.setProperty(cssVar, value);
            console.warn(cssVar);
        }
    }
}

provide("isMinimap", props.isMinimap);
</script>

<template>
    <div
        class="easy-page"
        :style="{
            'font-family': page.fontFamily,
            ...pageColors,
        }"
        :data-is-minimap="isMinimap"
        :data-dnd-container="!isMinimap"
        :class="[!hasSections ? 'h-screen' : '', isMinimap ? 'pointer-events-none' : '']"
    >
        <div
            v-if="!isMinimap"
            data-dnd="drop-indicator"
            class="h-[2px] bg-[#E46B41] w-full pointer-events-none absolute z-50 hidden"
        ></div>
        <draggable
            v-if="hasSections"
            v-model="sections"
            item-key="id"
            handle=".section-drag-handle"
            ghost-class="opacity-50"
            :disabled="isMinimap"
        >
            <template #item="{ element: section, index }">
                <EditableSection
                    :section-id="section.id"
                    :is-first="index === 0"
                    :is-last="index === sections.length - 1"
                    :enabled="!isDragging"
                >
                    <ToolProjectSection
                        v-if="section.kind === PAGE_SECTION_KINDS.TOOL && !section.isHidden"
                        :type="section.type"
                        :uuid="section?.projectData?.uuid"
                        :projectName="section?.projectData?.name"
                        :webId="section.webId"
                        :fontFamily="page.fontFamily"
                        :styleValues="makeStyleValues(section.styleValues || {})"
                        :isProjectPublished="section?.projectData?.access === 'published'"
                    />
                    <component
                        v-if="
                            section.kind === PAGE_SECTION_KINDS.BLOCK &&
                            !section.isHidden &&
                            blockComponents[section.type][section.typeId]
                        "
                        :is="blockComponents[section.type][section.typeId]"
                        :block="section"
                        :pageColors="pageColors"
                        :is-preview="preview"
                    />
                </EditableSection>
            </template>
        </draggable>
        <CardEmpty
            v-if="!hasSections"
            class="w-full"
            :title="$t('page.empty.title')"
            :icon="PageIcon"
            :ctaLabel="$t('page.empty.cta')"
            @add="addSection"
        />
    </div>
</template>
